import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Modal,
  ScrollView,
  ActivityIndicator,
  Dimensions,
} from 'react-native';
import { AntDesign, MaterialIcons, Feather } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { validateBulkOrders, queueOrders, resetBoxChamp } from '../../actions/boxChampActions';
import s from '../../style/boxChampCalculateModal';

const GROUPS = [
  {
    key: 'ready',
    label: 'ready for box calculation',
    icon: <AntDesign name="checkcircle" size={18} color="#1CBB77" />,
  },
  {
    key: 'needs_dims',
    label: '— items missing dimensions or weight',
    icon: <AntDesign name="warning" size={18} color="#f39c12" />,
  },
  {
    key: 'oversize',
    label: '— items too large for any box',
    icon: <AntDesign name="closecircle" size={18} color="#e74c3c" />,
  },
  {
    key: 'no_items',
    label: '— no items on order',
    icon: <AntDesign name="warning" size={18} color="#f39c12" />,
  },
  {
    key: 'scanning_started',
    label: '— already scanned or in progress',
    icon: <AntDesign name="warning" size={18} color="#f39c12" />,
  },
];

const BoxChampCalculateModal = ({ visible, onClose, orderIds, alertBox }) => {
  const dispatch = useDispatch();
  const { validating, validationResult, validationError, queuing } = useSelector(
    (state) => state.boxChamp
  );

  const [expanded, setExpanded] = useState({});
  const windowWidth = Dimensions.get('window').width;
  const orderIdsKey = (orderIds || []).join(',');

  useEffect(() => {
    if (visible && orderIds?.length > 0) {
      setExpanded({});
      dispatch(resetBoxChamp());
      dispatch(validateBulkOrders(orderIds));
    }
  }, [visible, orderIdsKey]);

  const handleQueue = () => {
    const readyOrders = validationResult?.orders?.ready || [];
    if (readyOrders.length === 0) return;

    dispatch(
      queueOrders(readyOrders, (queued) => {
        onClose();
        if (alertBox) alertBox(`${queued} order(s) queued for box calculation`, true);
      })
    );
  };

  const handleRetry = () => {
    dispatch(resetBoxChamp());
    dispatch(validateBulkOrders(orderIds));
  };

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderLoading = () => (
    <View style={s.loadingContainer} testID="bc-modal-loading">
      <ActivityIndicator size="large" color="#1CBB77" />
      <Text style={s.loadingText}>Validating orders...</Text>
    </View>
  );

  const renderQueuing = () => (
    <View style={s.loadingContainer} testID="bc-modal-queuing">
      <ActivityIndicator size="large" color="#1CBB77" />
      <Text style={s.loadingText}>Queuing orders for calculation...</Text>
    </View>
  );

  const renderError = () => (
    <View style={s.errorContainer} testID="bc-modal-error">
      <MaterialIcons name="error-outline" size={32} color="#e74c3c" />
      <Text style={s.errorText}>{validationError}</Text>
      <TouchableOpacity testID="bc-modal-retry" onPress={handleRetry} style={s.retryButton}>
        <Text style={s.retryButtonText}>Retry</Text>
      </TouchableOpacity>
    </View>
  );

  const renderGroup = ({ key, label, icon }) => {
    const orders = validationResult?.orders?.[key] || [];
    const count = orders.length;
    if (count === 0) return null;

    const isExpanded = expanded[key];

    return (
      <View key={key}>
        <TouchableOpacity
          testID={`bc-group-${key}`}
          style={s.groupRow}
          onPress={() => toggleExpand(key)}
        >
          <View style={s.groupIcon}>{icon}</View>
          <Text style={s.groupLabel}>
            <Text style={{ fontFamily: 'Poppins_600SemiBold' }}>{count}</Text>
            {` ${count === 1 ? 'order' : 'orders'} ${label}`}
          </Text>
          <Feather
            name={isExpanded ? 'chevron-up' : 'chevron-down'}
            size={16}
            color="#9aafc0"
            style={s.groupChevron}
          />
        </TouchableOpacity>

        {isExpanded && (
          <View style={s.orderList} testID={`bc-group-${key}-list`}>
            {orders.map((order) => (
              <View key={order.id} style={s.orderRow}>
                <Text style={s.orderText}>{order.increment_id || `#${order.id}`}</Text>
              </View>
            ))}
          </View>
        )}
      </View>
    );
  };

  const renderGroups = () => (
    <View testID="bc-modal-groups">
      <ScrollView style={s.scrollArea}>{GROUPS.map(renderGroup)}</ScrollView>
    </View>
  );

  const renderContent = () => {
    if (validating) return renderLoading();
    if (queuing) return renderQueuing();
    if (validationError) return renderError();
    return renderGroups();
  };

  const readyCount = validationResult?.summary?.ready || 0;
  const showQueueButton = !validating && !queuing && !validationError && validationResult;

  const renderQueueButton = () => {
    if (readyCount === 0) {
      return (
        <TouchableOpacity testID="bc-modal-queue-btn" style={s.queueButtonDisabled} disabled>
          <Text style={s.queueButtonTextDisabled}>No eligible orders</Text>
        </TouchableOpacity>
      );
    }
    return (
      <TouchableOpacity testID="bc-modal-queue-btn" style={s.queueButton} onPress={handleQueue}>
        <Text style={s.queueButtonText}>
          {`Queue ${readyCount} Order${readyCount === 1 ? '' : 's'} for Calculation`}
        </Text>
      </TouchableOpacity>
    );
  };

  const getTitle = () => {
    if (queuing) return 'Queuing orders...';
    return `Calculate Boxes — ${orderIds?.length || 0} orders`;
  };

  if (!visible) return null;

  return (
    <Modal transparent visible={visible} animationType="fade" onRequestClose={onClose}>
      <View style={s.overlay}>
        <View style={[s.dialog, { width: windowWidth >= 900 ? '55%' : '95%' }]}>
          {/* Header */}
          <View style={s.header}>
            <Text style={s.headerTitle}>{getTitle()}</Text>
            <TouchableOpacity testID="bc-modal-close" onPress={onClose} style={s.closeIcon}>
              <AntDesign name="close" size={20} color="#E0E7EF" />
            </TouchableOpacity>
          </View>

          {/* Body */}
          <View style={s.body}>{renderContent()}</View>

          {/* Footer */}
          <View style={s.footer}>
            <TouchableOpacity testID="bc-modal-cancel" onPress={onClose} style={s.cancelButton}>
              <Text style={s.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
            {showQueueButton && renderQueueButton()}
          </View>
        </View>
      </View>
    </Modal>
  );
};

BoxChampCalculateModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  orderIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  alertBox: PropTypes.func,
};

export default BoxChampCalculateModal;
